import type {
  BehaviorFunction,
  BehaviorSubject,
  Goal,
  GoalDomain,
  GoalType,
  Status,
} from "../types/aba";

// ---------------------------------------------------------------------------
// Enum casing normalization
// ---------------------------------------------------------------------------
//
// TEMPORARY WORKAROUND for backend bug ANOW-6956 — DELETE this whole file
// (and its two call sites in useGetDetailGoal / useGetDetailTarget) once
// that ticket is resolved.
//
// The bug: the Goal object embedded in `GET /targets/:id` (and returned by
// `GET /goals/:id`) serializes its enum fields in SCREAMING_SNAKE_CASE
// (e.g. "CAREGIVER", "IN_PROGRESS") instead of the documented kebab-case
// contract (`BehaviorSubject`, `GoalType`, `GoalDomain`, `Status` in
// api/types/aba.ts all expect lowercase-kebab). Every
// `behavior_subject === "caregiver"` style comparison across Goals & Targets
// silently fails against the raw value, e.g. hiding no prompt-hierarchy UI
// for caregiver goals, or `BEHAVIOR_SUBJECT_LABELS` lookups rendering blank.
// `normalizeGoal` corrects the casing once at the data-fetching boundary.
// ---------------------------------------------------------------------------

const VALID_BEHAVIOR_SUBJECTS: ReadonlySet<BehaviorSubject> = new Set(["client", "caregiver"]);
const VALID_GOAL_TYPES: ReadonlySet<GoalType> = new Set([
  "skill-acquisition",
  "behavior-reduction",
]);
const VALID_GOAL_DOMAINS: ReadonlySet<GoalDomain> = new Set([
  "adaptive",
  "behavior-reduction",
  "communication",
  "community",
  "executive-functioning",
  "social-skills",
]);
const VALID_STATUSES: ReadonlySet<Status> = new Set([
  "not-active",
  "in-progress",
  "on-hold",
  "met",
  "discontinued",
  "archived",
]);
const VALID_BEHAVIOR_FUNCTIONS: ReadonlySet<BehaviorFunction> = new Set([
  "attention",
  "escape",
  "tangible",
  "sensory",
]);

/**
 * Lowercases and dash-normalizes a raw enum string (e.g. "IN_PROGRESS" ->
 * "in-progress"), then checks the result against the known-valid set for
 * that field. Returns the corrected value when it's now recognized;
 * otherwise returns `raw` unchanged so genuinely unexpected data stays
 * visibly as-is rather than being silently coerced to a guessed default.
 */
function normalizeEnumValue<T extends string>(raw: T, validValues: ReadonlySet<T>): T {
  if (typeof raw !== "string") return raw;
  const normalized = raw.toLowerCase().replace(/_/g, "-") as T;
  return validValues.has(normalized) ? normalized : raw;
}

/**
 * Normalizes the casing of every enum-valued field on a `Goal` read from the
 * API (`domain`, `goal_type`, `behavior_subject`, `status`,
 * `hypothesized_functions`). Safe to call on goals that are already
 * correctly cased — normalization is a no-op in that case. See the section
 * comment above for why this exists.
 */
export function normalizeGoal(goal: Goal): Goal {
  return {
    ...goal,
    domain: normalizeEnumValue(goal.domain, VALID_GOAL_DOMAINS),
    goal_type: normalizeEnumValue(goal.goal_type, VALID_GOAL_TYPES),
    behavior_subject: normalizeEnumValue(goal.behavior_subject, VALID_BEHAVIOR_SUBJECTS),
    status: normalizeEnumValue(goal.status, VALID_STATUSES),
    hypothesized_functions: goal.hypothesized_functions?.map((f) =>
      normalizeEnumValue(f, VALID_BEHAVIOR_FUNCTIONS)
    ),
  };
}
